import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import { toast } from 'react-toastify';
import { updateProductWishlistStatus } from './product-slice';
import Cookies from 'js-cookie';

const isAuthenticated = () => !!Cookies.get('artote_token');

// Fetch wishlist
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue, getState }) => {
    if (!isAuthenticated()) {
      const state = getState();
      const currentItems = state.wishlist.wishlistItems;
      // Aggressively refresh ALL items to ensure data integrity
      const promises = currentItems.map(async (item) => {
        let idToFetch = null;
        if (typeof item === 'string') {
          idToFetch = item;
        } else if (item && (item.id || item._id)) {
          idToFetch = item.id || item._id;
        }

        if (idToFetch) {
          try {
            // Correct endpoint verified from product-slice.js
            const res = await axiosInstance.get(`/product/detail/${idToFetch}`);
            if (res.status === 200) {
              const resData = res.data;
              let productData = null;
              if (resData && resData.data) {
                productData = Array.isArray(resData.data) ? resData.data[0] : resData.data;
              } else if (resData) {
                productData = resData;
              }

              if (productData) {
                return productData;
              }
            }
          } catch (e) {
            console.warn(`Failed to refresh item ${idToFetch}`, e);
          }
        }
        // If fetch fails but we had an object, keep the object (fallback).
        // If we only had a string and fetch failed, return null (filter out).
        return typeof item === 'object' ? item : null;
      });

      const results = await Promise.all(promises);
      const cleanItems = results.filter(i => i !== null);

      return { isLocal: true, items: cleanItems };
    }
    try {
      const response = await axiosInstance.get('/product/wishlist');
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wishlist');
    }
  }
);

// Get wishlist count
export const fetchWishlistCount = createAsyncThunk(
  'wishlist/fetchWishlistCount',
  async (_, { rejectWithValue, getState }) => {
    if (!isAuthenticated()) {
      const state = getState();
      const validItems = state.wishlist.wishlistItems.filter(item => typeof item === 'object' && item !== null);
      return { count: validItems.length, isLocal: true };
    }
    try {
      const response = await axiosInstance.get('/product/wishlist/count');
      return response.data.data || { count: 0 };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wishlist count');
    }
  }
);

// Add to wishlist
export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (product, { dispatch, rejectWithValue, getState }) => {
    const productId = product.id || product._id;

    if (!isAuthenticated()) {
      const state = getState();
      const exists = state.wishlist.wishlistItems.find(item => {
        const pId = item.product?._id || item.id || item._id;
        return pId === productId;
      });

      if (exists) {
        toast.info('Already in wishlist', { position: 'top-right' });
        return { isLocal: true, exists: true };
      }

      dispatch(updateProductWishlistStatus({ productId, isWishlist: true }));
      toast.success('Added to wishlist', { position: 'top-right' });

      // We store the product directly or wrapped?
      // Wishlist.js handles "wishlistItem.product || wishlistItem"
      // So storing "product" directly is fine.
      return { item: product, isLocal: true };
    }

    try {
      const response = await axiosInstance.post(`/product/wishlist/${productId}`);

      // Update product wishlist status in product slice
      dispatch(updateProductWishlistStatus({ productId, isWishlist: true }));

      toast.success('Added to wishlist', { position: 'top-right' });

      // Fetch updated wishlist
      dispatch(fetchWishlist());

      return response.data.data;
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to add to wishlist',
        { position: 'top-right' }
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

// Remove from wishlist
export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId, { dispatch, rejectWithValue }) => {
    if (!isAuthenticated()) {
      dispatch(updateProductWishlistStatus({ productId, isWishlist: false }));
      toast.success('Removed from wishlist', { position: 'top-right' });
      return { id: productId, isLocal: true };
    }

    try {
      await axiosInstance.delete(`/product/wishlist/${productId}`);
      dispatch(updateProductWishlistStatus({ productId, isWishlist: false }));
      dispatch(fetchWishlist());
      toast.success('Removed from wishlist', { position: 'top-right' });
      return productId;
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to remove from wishlist',
        { position: 'top-right' }
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from wishlist');
    }
  }
);

export const deleteFromWishlist = removeFromWishlist;

// Delete all from wishlist
export const deleteAllFromWishlist = createAsyncThunk(
  'wishlist/deleteAllFromWishlist',
  async (_, { getState, dispatch, rejectWithValue }) => {
    if (!isAuthenticated()) {
      const { wishlistItems } = getState().wishlist;
      wishlistItems.forEach(item => {
        const productId = item.product?._id || item.id || item._id;
        dispatch(updateProductWishlistStatus({ productId, isWishlist: false }));
      });
      toast.success('Wishlist cleared', { position: 'top-right' });
      return { isLocal: true };
    }

    try {
      const { wishlistItems } = getState().wishlist;

      // Delete all items one by one (Optimization: API should ideally have a clear endpoint)
      await Promise.all(
        wishlistItems.map(item => {
          const productId = item.product?._id || item._id;
          return axiosInstance.delete(`/product/wishlist/${productId}`);
        })
      );

      // Update all products' wishlist status
      wishlistItems.forEach(item => {
        const productId = item.product?._id || item._id;
        dispatch(updateProductWishlistStatus({ productId, isWishlist: false }));
      });

      toast.success('Wishlist cleared', { position: 'top-right' });
      return true;
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to clear wishlist',
        { position: 'top-right' }
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to clear wishlist');
    }
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    wishlistItems: [],
    wishlistCount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    clearWishlist: (state) => {
      state.wishlistItems = [];
      state.wishlistCount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isLocal) {
          // If we filtered items in the thunk, update state to remove invalid ones
          if (action.payload.items) {
            state.wishlistItems = action.payload.items;
            state.wishlistCount = action.payload.items.length;
          }
          return;
        }
        state.wishlistItems = action.payload;
        state.wishlistCount = action.payload.length;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch wishlist count
      .addCase(fetchWishlistCount.fulfilled, (state, action) => {
        state.wishlistCount = action.payload.count || 0;
      })
      // Add to wishlist
      .addCase(addToWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isLocal) {
          if (!action.payload.exists) {
            state.wishlistItems.push(action.payload.item);
            state.wishlistCount = state.wishlistItems.length;
          }
        }
      })
      .addCase(addToWishlist.rejected, (state) => {
        state.loading = false;
      })
      // Remove from wishlist
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        if (action.payload.isLocal || !isAuthenticated()) {
          const idToCheck = action.payload.id || action.payload;
          state.wishlistItems = state.wishlistItems.filter(
            item => {
              const itemId = item.product?._id || item.id || item._id;
              return itemId !== idToCheck;
            }
          );
          state.wishlistCount = state.wishlistItems.length;
        } else {
          // API mode returns productId
          state.wishlistItems = state.wishlistItems.filter(
            item => {
              const itemId = item.product?._id || item._id;
              return itemId !== action.payload;
            }
          );
          state.wishlistCount = state.wishlistItems.length;
        }
      })
      // Delete all from wishlist
      .addCase(deleteAllFromWishlist.fulfilled, (state) => {
        state.wishlistItems = [];
        state.wishlistCount = 0;
      });
  }
});

export const { clearWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;
